<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, Col, Modal, ModalBody, Row } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import {
    companyName,
    companyUrl,
    fontBase,
    fontHeadings,
    iconUrl,
    logoUrl,
    updateBranding
  } from '@client/stores/brand';

  import Colors from './colors.svelte';
  import Intro from './intro.svelte';
  import Logos from './logos.svelte';
  import Preview from './preview.svelte';
  import Review from './review.svelte';
  import Typography from './typography.svelte';

  export let show = false;
  export let step = 0;

  let uploading = false;

  const navigate = useNavigate();
  const { addNotification } = getNotificationsContext();

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onSelect = async (template) => {
    navigate(`new/${template.id}`);
  };

  const INTRO = 0;
  const COLORS = 1;
  const FONTS = 2;
  const LOGOS = 3;
  const REVIEW = 4;

  const toggle = () => {
    show = false;
  };

  const onNextClick = async () => {
    if (step === REVIEW) {
      const success = await updateBranding();

      if (success) {
        notify($format('label.SUCCESS.BRAND_WIZARD'));

        toggle();
      } else {
        notify($format('label.FAILURE.BRAND_WIZARD'), 'error');
      }
    } else {
      step++;
    }
  };

  $: if (show) {
    step = 0;
  }

  // Colors are always enabled with default values, but TODO check invalid values:
  $: nextDisabled =
    (step === INTRO && !($companyName && $companyUrl)) ||
    (step === FONTS && !($fontBase && $fontHeadings)) ||
    (step === LOGOS && (uploading || !($logoUrl && $iconUrl)));
</script>

{#if show}
  <Modal class="modal-wizard" isOpen size="xl" fullscreen="lg" {toggle} {...$$restProps}>
    <ModalBody class="p-0">
      <div class="position-absolute top-0 end-0 mt-1 me-0">
        <Button color="link" on:click={toggle} class="modal-close">
          <Icon name="x" />
        </Button>
      </div>

      <Row>
        <Col md={6} class="settings-pane">
          {#if step === INTRO}
            <Intro />
          {:else if step === COLORS}
            <Colors />
          {:else if step === FONTS}
            <Typography />
          {:else if step === REVIEW}
            <Review />
          {:else if step === LOGOS}
            <Logos bind:uploading />
          {/if}
        </Col>
        <Col md={6} class="preview-pane">
          <Preview />
        </Col>
      </Row>

      <div class="steps position-absolute top-0 end-0 mt-5 me-5">
        <div class="steps-pill rounded text-uppercase py-1 px-2">
          {$format('label.BRAND_WIZARD_STEP', { values: { step: step + 1, total: 5 } })}
        </div>
      </div>
      <div class="actions">
        {#if step > INTRO}
          <Button color="link" class="me-2" on:click={() => (step = step - 1)}>
            <Icon name="arrow-left" />
            {$format('label.BACK')}
          </Button>
        {:else}
          <Button color="link" class="me-2" on:click={toggle}>
            {$format('label.CANCEL')}
          </Button>
        {/if}

        <Button color="primary" on:click={onNextClick} disabled={nextDisabled}>
          {#if step === INTRO}
            {$format('label.NEXT')} <Icon name="arrow-right" /> {$format('label.BRAND_WIZARD_STEP_COLORS')}
          {:else if step === COLORS}
            {$format('label.NEXT')} <Icon name="arrow-right" /> {$format('label.BRAND_WIZARD_STEP_TYPOGRAPHY')}
          {:else if step === FONTS}
            {$format('label.NEXT')} <Icon name="arrow-right" /> {$format('label.BRAND_WIZARD_STEP_LOGOS')}
          {:else if step === LOGOS}
            {$format('label.NEXT')} <Icon name="arrow-right" /> {$format('label.BRAND_WIZARD_STEP_REVIEW')}
          {:else}
            {$format('label.BRAND_WIZARD_SAVE')}
          {/if}
        </Button>
      </div>
    </ModalBody>
  </Modal>
{/if}

<style lang="scss">
  :global(.modal-close) {
    color: var(--color-gray-600);
  }

  .steps-pill {
    border: 1px solid var(--color-gray-300);
    color: var(--color-gray-600);
    font-size: 0.75rem;
    margin-right: -8px;
  }

  .actions {
    bottom: 0;
    display: flex;
    justify-content: flex-end;
    margin: var(--margin-jumbo);
    position: absolute;
    right: -8px;
  }

  :global(.settings-pane) {
    border-right: 1px solid var(--border-color);
    min-height: 77vh;
    padding: var(--padding-jumbo);
  }

  :global(.preview-pane) {
    padding: var(--padding-jumbo);
  }

  @media only screen and (max-width: 991px) {
    :global(.settings-pane) {
      min-height: 100vh;
    }
  }

  @media only screen and (max-width: 600px) {
    :global(.settings-pane) {
      border-bottom: 1px solid var(--border-color);
      border-right: 0;
      padding-bottom: var(--padding-xlarge);
      min-height: auto;
    }

    :global(.preview-pane) {
      padding-bottom: 0;
      padding-top: var(--padding-large);
    }

    .actions {
      position: relative;
    }
  }
</style>
